import { Link, useParams } from "react-router-dom";
import { Reveal } from "@/components/motion/Reveal";
import { ArrowUpLeft, ArrowUpRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { imgSrc, money } from "@/lib/api";

export default function KingsleyCategory() {
  const { categoryId } = useParams();
  const { category: getCat, productsIn, loading } = useStore();
  const category = getCat(categoryId);
  const products = productsIn(categoryId);

  if (loading) return <div className="min-h-screen bg-[#EDECE9]" />;
  if (!category) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-[#EDECE9] px-6 text-center">
        <h1 className="font-display text-3xl font-extralight text-ink">Room not found.</h1>
        <Link to="/kingsley" className="link-underline mt-6 overline text-stone">Back to the boutique</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDECE9] px-6 pb-28 pt-32 md:px-12 md:pt-40">
      <div className="mx-auto max-w-[1500px]">
        <Reveal>
          <Link to="/kingsley" data-testid="kingsley-back-boutique" className="group inline-flex items-center gap-2 text-stone transition-colors hover:text-ink">
            <ArrowUpLeft className="h-4 w-4 transition-transform duration-500 group-hover:-translate-x-0.5" />
            <span className="overline">The boutique</span>
          </Link>
          <h1 className="mt-8 font-display text-5xl font-extralight tracking-tight text-ink md:text-7xl">{category.title}</h1>
          <p className="mt-5 max-w-xl font-body text-sm leading-relaxed text-stone md:text-base">{category.note}</p>
          {products.length > 0 && products.every((p) => p.one_of_a_kind) && (
            <p data-testid="kingsley-one-of-a-kind" className="overline mt-4 text-clay">Every piece is one of a kind</p>
          )}
        </Reveal>

        {products.length === 0 ? (
          <p className="mt-16 font-display text-xl font-extralight text-stone">More pieces landing soon.</p>
        ) : (
          <div className="mt-14 grid grid-cols-2 gap-5 md:grid-cols-3 md:gap-8">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05}>
                <Link to={`/kingsley/p/${p.id}`} data-testid={`kingsley-product-${p.id}`} className="group block">
                  <div className="relative overflow-hidden bg-[#333336]">
                    <img src={imgSrc(p.images[0])} alt={p.name} className={`hover-media aspect-[3/4] w-full object-cover object-top ${p.stock === 0 ? "opacity-70 grayscale-[35%]" : ""}`} />
                    {p.stock === 0 && (
                      <span data-testid={`kingsley-product-sold-${p.id}`} className="absolute right-3 top-3 bg-ink px-3 py-1 font-mono text-[0.55rem] uppercase tracking-widest text-paper">Sold</span>
                    )}
                    {p.kind === "ready-to-wear" && (
                      <span className="absolute left-3 top-3 rounded-full bg-paper/90 px-3 py-1 font-mono text-[0.55rem] uppercase tracking-widest text-ink">
                        Ready to wear
                      </span>
                    )}
                  </div>
                  <div className="mt-3 flex items-start justify-between gap-2">
                    <div>
                      <h2 className="font-display text-lg font-extralight tracking-tight text-ink md:text-xl">{p.name}</h2>
                      <p data-testid={`kingsley-product-price-${p.id}`} className="overline mt-1 text-[0.56rem] text-stone">{p.stock === 0 ? "Sold" : p.one_of_a_kind ? `${money(p.price)} · one of a kind` : money(p.price)}</p>
                    </div>
                    <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-clay transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
